'use client';

import { Box, Button, Grid, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { IconReload } from '@tabler/icons-react';
import { useEffect, useId, useMemo, useState } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  applyAprOverride,
  ExtendedLenderTier,
  filterAndSortTiers,
} from '@/lib/loanFilteringHelper';
import { ComponentLoanLoanComparison, Lender, LenderTier } from '@/types';

import AdvancedFiltersModal, { AdvancedFilters } from './AdvancedFilters';
import FilterBar from './FilterBar';
import LoanCard from './LoanCard';
import LoanRow from './LoanRow';

type Props = { block: ComponentLoanLoanComparison };

type Ranges = {
  loanAmount: [number | null, number | null];
  loanTerm: [number | null, number | null];
  timeInBiz: [number | null, number | null];
  monthlySales: [number | null, number | null];
  apr: [number | null, number | null];
};

export function normalizePurpose(value?: string | null): string {
  if (!value) return '';

  let v = value
    .replace(/_/g, ' ') // underscores → spaces
    .replace(/\s{2,}/g, ' ') // collapse extra spaces
    .trim();

  v = v.replace(/\b[Aa]nd\b/g, '&');

  v = v.replace(/\bday to day\b/i, 'Day-to-day').replace(/\bnew fit out\b/i, 'New Fit-out');

  const lowercaseWords = ['a', 'and', 'or', 'to', 'for', 'in', 'of', 'the', 'be'];

  v = v
    .split(' ')
    .map((word, index) => {
      const lower = word.toLowerCase();

      // Preserve special tokens
      if (/day-to-day/i.test(word)) return 'Day-to-day';
      if (/fit-out/i.test(word)) return 'Fit-out';
      if (word === '&') return '&';

      // Keep connector words lowercase (not at start)
      if (index > 0 && lowercaseWords.includes(lower)) return lower;

      // Capitalize first letter, keep rest lower
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');

  return v;
}

export function normalizeProductType(value?: string | null): string {
  if (!value) return '';

  // Normalize product types: replace / _ - with spaces, collapse spaces, trim
  let v = value
    .replace(/[\/_-]/g, ' ') // replace / _ - with spaces
    .replace(/\s+/g, ' ') // collapse multiple spaces
    .trim();

  // Capitalize first letter of each word
  v = v
    .split(' ')
    .map((word) => {
      if (!word) return '';
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');

  return v;
}

// Map lender data to consistent format (works for both API and GraphQL props)
function mapLender(l: any): Lender {
  return {
    documentId: l.documentId || l.id,
    lenderRef: l.lenderRef,
    lenderName: l.lenderName,
    lenderLogo: l.lenderLogo,
    minLoanAmount: l.minLoanAmount,
    maxLoanAmount: l.maxLoanAmount,
    minLoanTerm: l.minLoanTerm,
    maxLoanTerm: l.maxLoanTerm,
    minTimeInBiz: l.minTimeInBiz,
    maxTimeInBiz: l.maxTimeInBiz,
    minMonthlySales: l.minMonthlySales,
    maxMonthlySales: l.maxMonthlySales,
    minApr: l.minApr,
    maxApr: l.maxApr,
    overrideApr: l.overrideApr,
    overrideMinApr: l.overrideMinApr,
    overrideMaxApr: l.overrideMaxApr,
    hideApr: l.hideApr,
    purposeAllowed: l.purposeAllowed,
    updated: l.updated,
    lenderTiers: (l.lenderTiers || []).map((t: any) => ({
      documentId: t.documentId || t.id,
      ...t,
    })),
  };
}

export default function LoanComparison({ block }: Props) {
  const theme = useTheme();
  const baseId = useId(); // Stable across SSR/hydration to avoid React #418
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const notNull = <T,>(v: T | null | undefined): v is T => v != null;

  // Initialize state with lenders from props if available
  const initialLenders = useMemo(() => {
    const filtered = (block?.Lenders ?? []).filter(notNull);
    return filtered.map(mapLender);
  }, [block?.Lenders]);

  const [lenders, setLenders] = useState<Lender[]>(initialLenders);
  const [loading, setLoading] = useState(initialLenders.length === 0);
  const [visibleCount, setVisibleCount] = useState(10);

  // Handle lenders: use from props if provided, otherwise fetch from API
  useEffect(() => {
    // If we have valid lenders from props, use them
    if (initialLenders.length > 0) {
      setLenders(initialLenders);
      setLoading(false);
      return;
    }

    // Otherwise, fetch from API
    // Only fetch if we don't have lenders from props
    if (!block.Lenders || block.Lenders.length === 0) {
      (async () => {
        setLoading(true);
        try {
          const res = await fetch(
            `${process.env.NEXT_PUBLIC_STRAPI_URL}/api/lenders?populate[lenderTiers][populate]=*`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
              },
            }
          );

          const json = await res.json();

          const mapped: Lender[] = json.data.map(mapLender);

          setLenders(mapped);
        } catch (err) {
          console.error('❌ Failed to fetch lenders:', err);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [block.Lenders, initialLenders]);

  const allTiers: ExtendedLenderTier[] = useMemo(() => {
    return block.IncludeLenderTiers
      ? lenders.flatMap((l, lenderIdx) =>
          (l.lenderTiers || [])
            .filter((t): t is LenderTier => Boolean(t?.documentId))
            .map((t, tierIdx) =>
              applyAprOverride({
                ...t,
                documentId: t.documentId ?? `temp-${baseId}-${lenderIdx}-${tierIdx}`,
                lenderName: l.lenderName ?? '',
                lenderLogo: l.lenderLogo ?? '',
                hideApr: Boolean(l.hideApr || t.hideApr),
              })
            )
        )
      : lenders.map((l) =>
          applyAprOverride({
            documentId: l.documentId,
            lenderName: l.lenderName ?? '',
            lenderLogo: l.lenderLogo ?? '',
            minLoanAmount: l.minLoanAmount ?? 0,
            maxLoanAmount: l.maxLoanAmount ?? 0,
            minLoanTerm: l.minLoanTerm ?? 0,
            maxLoanTerm: l.maxLoanTerm ?? 0,
            minTimeInBiz: l.minTimeInBiz ?? 0,
            maxTimeInBiz: l.maxTimeInBiz ?? 0,
            minMonthlySales: l.minMonthlySales ?? 0,
            maxMonthlySales: l.maxMonthlySales ?? 0,
            minApr: l.minApr ?? 0,
            maxApr: l.maxApr ?? 0,
            overrideApr: l.overrideApr,
            overrideMinApr: l.overrideMinApr,
            overrideMaxApr: l.overrideMaxApr,
            purposeAllowed: l.purposeAllowed ?? [],
            // Lender schema has no productTypesAllowed field — roll up from tiers
            // so DefaultProductType / productType filters still work in lender view.
            productTypesAllowed: Array.from(
              new Set((l.lenderTiers || []).flatMap((t: any) => t?.productTypesAllowed || []))
            ),
            hideApr: l.hideApr ?? false,
          })
        );
  }, [lenders, block, baseId]);

  const [sortBy, setSortBy] = useState(block.DefaultSort || 'interestLowToHigh');
  const [view, setView] = useState<'list' | 'grid'>(
    (block.DefaultView as 'list' | 'grid') || 'list'
  );

  const effectiveView = isMobile ? 'grid' : view;

  const ranges: Ranges = useMemo(() => {
    const min = (arr: number[]) => (arr.length ? Math.min(...arr) : null);
    const max = (arr: number[]) => (arr.length ? Math.max(...arr) : null);

    return {
      loanAmount: [
        min(allTiers.map((t) => Number(t.minLoanAmount || 0))),
        max(allTiers.map((t) => Number(t.maxLoanAmount || 0))),
      ],
      loanTerm: [
        min(allTiers.map((t) => Number(t.minLoanTerm || 0))),
        max(allTiers.map((t) => Number(t.maxLoanTerm || 0))),
      ],
      timeInBiz: [
        min(allTiers.map((t) => Number(t.minTimeInBiz || 0))),
        max(allTiers.map((t) => Number(t.maxTimeInBiz || 0))),
      ],
      monthlySales: [
        min(allTiers.map((t) => Number(t.minMonthlySales || 0))),
        max(allTiers.map((t) => Number(t.maxMonthlySales || 0))),
      ],
      apr: [
        min(allTiers.map((t) => Number(t.minApr || 0))),
        max(allTiers.map((t) => Number(t.maxApr || 0))),
      ],
    };
  }, [allTiers]);

  const purposes = useMemo(() => {
    const all = allTiers.flatMap((t) => t.purposeAllowed || []);
    return Array.from(new Set(all)).sort();
  }, [allTiers]);

  const productTypes = useMemo(() => {
    const all = allTiers.flatMap((t) => t.productTypesAllowed || []);
    const cleaned = all.map((type) => type.replace(/\//g, ' ').replace(/\s+/g, ' ').trim());
    return Array.from(new Set(cleaned)).sort();
  }, [allTiers]);

  const includeLenderTiers = useMemo(() => {
    return block?.IncludeLenderTiers ?? false;
  }, [block?.IncludeLenderTiers]);

  const [advanced, setAdvanced] = useState<AdvancedFilters>({
    lenders: [],
    purposes: block?.DefaultPurpose ? [normalizePurpose(block?.DefaultPurpose)] : [],
    productTypes: block?.DefaultProductType
      ? [normalizeProductType(block?.DefaultProductType)]
      : [],
    loanAmount: block?.DefaultLoanAmount
      ? [block.DefaultLoanAmount, ranges.loanAmount[1]]
      : ranges.loanAmount,
    loanTerm: ranges.loanTerm,
    timeInBiz: ranges.timeInBiz,
    monthlySales: ranges.monthlySales,
    apr: ranges.apr,
    hideApr: false,
  });

  const [showModal, setShowModal] = useState(false);

  const filteredTiers = useMemo(() => {
    // First filter by lenders if specified (LoanComparison-specific)
    let result = [...allTiers];
    if (advanced.lenders.length) {
      result = result.filter((t) => advanced.lenders.includes(t.lenderName || ''));
    }

    // Then apply standard filtering and sorting
    return filterAndSortTiers(
      result,
      {
        purposes: advanced.purposes.length ? advanced.purposes : undefined,
        productTypes: advanced.productTypes.length ? advanced.productTypes : undefined,
        loanAmount: advanced.loanAmount,
        loanTerm: advanced.loanTerm,
        timeInBiz: advanced.timeInBiz,
        monthlySales: advanced.monthlySales,
        apr: advanced.apr,
        hideApr: advanced.hideApr,
      },
      sortBy
    );
  }, [allTiers, advanced, sortBy]);

  const mostRecentUpdate = useMemo(() => {
    const timestamps = lenders
      .map((l) => l.updated)
      .filter(Boolean)
      .map((date) => new Date(date!).getTime());

    const latest = Math.max(...timestamps);
    return isFinite(latest) ? new Date(latest) : null;
  }, [lenders]);

  // Set DefaultLoanAmount when ranges are ready and DefaultLoanAmount is provided
  useEffect(() => {
    const defaultAmount = block?.DefaultLoanAmount;
    if (
      defaultAmount != null &&
      typeof defaultAmount === 'number' &&
      ranges.loanAmount[1] != null &&
      ranges.loanAmount[1] > 0 &&
      advanced.loanAmount[0] === ranges.loanAmount[0] &&
      advanced.loanAmount[0] !== defaultAmount
    ) {
      const amount: number = defaultAmount;
      setAdvanced((prev) => ({
        ...prev,
        loanAmount: [amount, Math.max(prev.loanAmount[1] || 0, ranges.loanAmount[1] || 0)],
      }));
    }
  }, [ranges.loanAmount, block?.DefaultLoanAmount, advanced.loanAmount]);

  useEffect(() => {
    setAdvanced((prev) => {
      const isDefault = !prev || (prev.loanAmount[0] === 0 && prev.loanAmount[1] === 0);

      if (isDefault) {
        return {
          ...prev,
          lenders: prev?.lenders ?? [],
          purposes: prev?.purposes?.length
            ? prev.purposes
            : block?.DefaultPurpose
              ? [normalizePurpose(block.DefaultPurpose)]
              : [],
          productTypes: prev?.productTypes?.length
            ? prev.productTypes
            : block?.DefaultProductType
              ? [normalizeProductType(block.DefaultProductType)]
              : [],
          loanAmount: block?.DefaultLoanAmount
            ? [block.DefaultLoanAmount, ranges.loanAmount[1]]
            : ranges.loanAmount,
          loanTerm: ranges.loanTerm,
          timeInBiz: ranges.timeInBiz,
          monthlySales: ranges.monthlySales,
          apr: ranges.apr,
          hideApr: false,
        };
      }
      return prev;
    });
  }, [ranges, block]);

  const clearFilters = () =>
    setAdvanced({
      lenders: [],
      purposes: [],
      productTypes: [],
      loanAmount: block?.DefaultLoanAmount
        ? [block.DefaultLoanAmount, ranges.loanAmount[1]]
        : ranges.loanAmount,
      loanTerm: ranges.loanTerm,
      timeInBiz: ranges.timeInBiz,
      monthlySales: ranges.monthlySales,
      apr: ranges.apr,
      hideApr: false,
    });

  if (!advanced) return null;

  if (loading)
    return (
      <CustomBackground
        backgroundOptions={{
          BackgroundColor: block.LoanComparisonBackground?.BackgroundColor ?? 'none',
          BackgroundOrbs: block.LoanComparisonBackground?.BackgroundOrbs ?? 'none',
        }}
        displayOptions={
          {
            PaddingTop: block?.LoanComparisonDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block?.LoanComparisonDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block?.LoanComparisonDisplayOptions?.PaddingInline ?? 'xs',
          } as StrapiDisplayOptions
        }
      >
        <Box>
          {block?.LoanComparisonTitle && (
            <Skeleton variant="text" width={320} height={52} sx={{ mb: 2 }} />
          )}

          {/* FilterBar placeholder */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
            <Skeleton variant="rounded" width={200} height={44} sx={{ borderRadius: 8 }} />
            <Skeleton variant="rounded" width={180} height={44} sx={{ borderRadius: 8 }} />
            <Skeleton variant="rounded" width={160} height={44} sx={{ borderRadius: 8 }} />
          </Box>

          {/* Cards container */}
          <Box
            sx={{
              backgroundColor: 'rgb(245, 244, 245)',
              borderRadius: 4,
              px: { xs: 1, sm: 2, md: 3 },
              py: { xs: 3, sm: 4 },
              mt: 4,
            }}
          >
            {Array.from({ length: 6 }).map((_, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: 2,
                  p: { xs: 2, sm: 3 },
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.common.white,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                {/* Logo */}
                <Skeleton
                  variant="rectangular"
                  width={100}
                  height={80}
                  sx={{ borderRadius: 1, flexShrink: 0 }}
                />
                {/* Stats */}
                <Box sx={{ flex: 1, width: '100%' }}>
                  <Skeleton variant="text" width="45%" height={28} sx={{ mb: 1 }} />
                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Skeleton variant="text" width={130} height={20} />
                    <Skeleton variant="text" width={130} height={20} />
                    <Skeleton variant="text" width={110} height={20} />
                  </Box>
                </Box>
                {/* CTA */}
                <Skeleton
                  variant="rounded"
                  width={160}
                  height={48}
                  sx={{ borderRadius: 999, flexShrink: 0 }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </CustomBackground>
    );

  return (
    <CustomBackground
      backgroundOptions={{
        BackgroundColor: block.LoanComparisonBackground?.BackgroundColor ?? 'none',
        BackgroundOrbs: block.LoanComparisonBackground?.BackgroundOrbs ?? 'none',
      }}
      displayOptions={
        {
          PaddingTop: block?.LoanComparisonDisplayOptions?.PaddingTop ?? 'md',
          PaddingBottom: block?.LoanComparisonDisplayOptions?.PaddingBottom ?? 'md',
          PaddingInline: block?.LoanComparisonDisplayOptions?.PaddingInline ?? 'xs',
        } as StrapiDisplayOptions
      }
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            mb: 2,
            gap: 1,
          }}
        >
          {block?.LoanComparisonTitle && (
            <Typography variant="h2" fontWeight={700}>
              {block.LoanComparisonTitle}
            </Typography>
          )}

          {mostRecentUpdate && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconReload size={16} stroke={1.5} style={{ color: theme.palette.text.secondary }} />
              <Typography
                variant="caption"
                sx={{
                  px: 1.5,
                  py: 0.25,
                  borderRadius: 1,
                  fontWeight: 400,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                }}
              >
                Last updated:{' '}
                {mostRecentUpdate.toLocaleDateString('en-AU', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </Typography>
            </Box>
          )}
        </Box>

        <GlobalBlockRenderer content={block.Description || []} />

        <FilterBar
          lenders={Array.from(
            new Set(
              lenders.map((l) => l.lenderName).filter((name): name is string => Boolean(name))
            )
          )}
          purposes={purposes}
          advanced={advanced}
          defaultLoanAmount={block.DefaultLoanAmount}
          setAdvanced={setAdvanced}
          sortBy={sortBy}
          setSortBy={setSortBy}
          view={view}
          setView={setView}
          onOpenAdvanced={() => setShowModal(true)}
          onClear={clearFilters}
          onToggleView={(mode) => setView(mode)}
        />

        <Box
          sx={{
            backgroundColor: 'rgb(245, 244, 245)',
            borderRadius: 4,
            boxShadow: '0px 6px 20px rgba(0,0,0,0.15)',
            px: { xs: 1, sm: 2, md: 3 },
            py: { xs: 3, sm: 4 },
            mt: 4,
          }}
        >
          <Grid container spacing={3}>
            {filteredTiers.slice(0, visibleCount).map((tier) => (
              <Grid
                item
                xs={12}
                sm={effectiveView === 'grid' ? 6 : 12}
                md={effectiveView === 'grid' ? 4 : 12}
                key={tier.documentId}
              >
                {effectiveView === 'list' ? <LoanRow tier={tier} /> : <LoanCard tier={tier} />}
              </Grid>
            ))}

            {filteredTiers.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center', width: '100%' }}>
                <Typography variant="body1" color="text.secondary">
                  No results found for the selected filters.
                </Typography>
              </Box>
            )}
          </Grid>

          {filteredTiers.length > visibleCount && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setVisibleCount((prev) => prev + 10);
                }}
                sx={{
                  borderRadius: 8,
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                }}
              >
                Load more ({Math.min(visibleCount + 10, filteredTiers.length)} of{' '}
                {filteredTiers.length})
              </Button>
            </Box>
          )}
        </Box>

        <AdvancedFiltersModal
          open={showModal}
          onClose={() => setShowModal(false)}
          filters={advanced}
          setFilters={setAdvanced}
          ranges={ranges}
          lenders={Array.from(
            new Set(
              lenders.map((l) => l.lenderName).filter((name): name is string => Boolean(name))
            )
          )}
          purposes={purposes}
          productTypes={productTypes}
          includeLenderTiers={includeLenderTiers}
        />
      </Box>
    </CustomBackground>
  );
}
